import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  Inventory,
  AccountBalance,
  Download,
  DateRange,
  PictureAsPdf,
  TableChart,
  GridOn,
  MoreVert,
  Check,
} from '@mui/icons-material';
import { reportsAPI } from '../../services/API';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const ExportMenu = ({ onExport, loading }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('csv');

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
    handleClose();
  };

  const exportOptions = [
    { format: 'csv', label: 'CSV Format', icon: <GridOn /> },
    { format: 'excel', label: 'Excel Format', icon: <TableChart /> },
    { format: 'pdf', label: 'PDF Format', icon: <PictureAsPdf /> },
  ];

  return (
    <>
      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
        onClick={handleClick}
        disabled={loading}
      >
        Export Report
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {exportOptions.map((option) => (
          <MenuItem
            key={option.format}
            onClick={() => handleExport(option.format)}
            selected={selectedFormat === option.format}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText primary={option.label} />
            {selectedFormat === option.format && <Check sx={{ ml: 2 }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const Reports = () => {
  const [tabValue, setTabValue] = useState(0);
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [profitLossReport, setProfitLossReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0],
  });

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchReports();
  }, [tabValue, dateRange]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      switch (tabValue) {
        case 0: // Sales Report
          const salesResponse = await reportsAPI.getSalesReport({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            groupBy: 'day',
          });
          setSalesReport(salesResponse.data.data);
          break;

        case 1: // Inventory Report
          const inventoryResponse = await reportsAPI.getInventoryReport();
          setInventoryReport(inventoryResponse.data.data);
          break;

        case 2: // Profit & Loss
          const profitLossResponse = await reportsAPI.getProfitLossReport({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          });
          setProfitLossReport(profitLossResponse.data.data);
          break;

        default:
          break;
      }
    } catch (error) {
      enqueueSnackbar('Error fetching report data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToCSV = (data, filename) => {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportToExcel = (data, filename) => {
    const wb = XLSX.utils.book_new();

    if (Array.isArray(data)) {
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Report');
    } else if (typeof data === 'object') {
      // Handle multiple sheets for complex reports
      Object.keys(data).forEach((sheetName, index) => {
        if (Array.isArray(data[sheetName])) {
          const ws = XLSX.utils.json_to_sheet(data[sheetName]);
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        }
      });
    }

    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToPDF = async (data, filename, reportType) => {
    const doc = new jsPDF();

    // Add header - add currency notation at the top
    doc.setFontSize(18);
    doc.text(`${reportType} Report`, 14, 22);
    doc.setFontSize(11);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 32);
    //doc.text(`All amounts in INR (\u20B9)`, 14, 42); // Add currency notation

    let yPos = 50; // Increased starting position

    switch (reportType) {
      case 'Sales':
        // Sales Summary
        doc.setFontSize(14);
        doc.text('Sales Summary', 14, yPos);
        yPos += 10;

        // Remove ₹ symbol from table data
        const salesSummaryData = [
          ['Total Sales', data.summary.totalSales.toString()],
          ['Total Revenue', data.summary.totalRevenue.toLocaleString()],
          ['Total Cost', data.summary.totalCost.toLocaleString()],
          ['Total Profit', data.summary.totalProfit.toLocaleString()],
        ];

        autoTable(doc, {
          startY: yPos,
          head: [['Metric', 'Value']],
          body: salesSummaryData,
          theme: 'striped',
          headStyles: { fillColor: [63, 81, 181] },
          columnStyles: {
            0: { fontStyle: 'bold' },
            1: { halign: 'right' }
          },
          didDrawPage: function (data) {
            // Add column headers
            doc.setFontSize(10);
            doc.text('INR', data.settings.margin.left + 120, yPos - 5);
          }
        });

        yPos = doc.lastAutoTable.finalY + 15;

        // Top Selling Medicines
        doc.setFontSize(14);
        doc.text('Top Selling Medicines', 14, yPos);

        const topSellingData = data.topSellingMedicines.map(med => [
          med.medicine.name.substring(0, 25), // Limit name length
          med.quantity.toString(),
          med.revenue.toFixed(2),
          med.cost.toFixed(2),
          (med.revenue - med.cost).toFixed(2)
        ]);

        autoTable(doc, {
          startY: yPos + 10,
          head: [['Medicine', 'Qty Sold', 'Revenue', 'Cost', 'Profit']],
          body: topSellingData,
          theme: 'grid',
          headStyles: { fillColor: [63, 81, 181] },
          styles: { fontSize: 9 },
          columnStyles: {
            0: { cellWidth: 45 },
            1: { cellWidth: 25, halign: 'center' },
            2: { cellWidth: 30, halign: 'right' },
            3: { cellWidth: 30, halign: 'right' },
            4: { cellWidth: 30, halign: 'right' }
          }
        });
        break;

      case 'Inventory':
        // Inventory Summary
        doc.setFontSize(14);
        doc.text('Inventory Summary', 14, yPos);
        yPos += 10;

        // Fixed: Using correct variable name
        const inventorySummaryData = [
          ['Total Medicines', data.summary.totalMedicines.toString()],
          ['Low Stock Items', data.summary.lowStockCount.toString()],
          ['Expiring Soon', data.summary.expiringCount.toString()],
          ['Total Value', data.summary.totalValue.toLocaleString()],
        ];

        autoTable(doc, {
          startY: yPos,
          head: [['Metric', 'Value']],
          body: inventorySummaryData,
          theme: 'striped',
          headStyles: { fillColor: [33, 150, 243] },
          columnStyles: {
            0: { fontStyle: 'bold' },
            1: { halign: 'right' }
          }
        });

        yPos = doc.lastAutoTable.finalY + 15;

        // Inventory by Category
        doc.setFontSize(14);
        doc.text('Inventory by Category', 14, yPos);

        const categoryData = data.inventoryByCategory.map(cat => [
          cat.category.substring(0, 25),
          cat.medicines.toString(),
          cat.totalQuantity.toString(),
          cat.totalValue.toLocaleString()
        ]);

        autoTable(doc, {
          startY: yPos + 10,
          head: [['Category', 'Medicines', 'Total Qty', 'Total Value']],
          body: categoryData,
          theme: 'grid',
          headStyles: { fillColor: [33, 150, 243] },
          styles: { fontSize: 9 },
          columnStyles: {
            0: { cellWidth: 50 },
            1: { cellWidth: 30, halign: 'center' },
            2: { cellWidth: 30, halign: 'center' },
            3: { cellWidth: 40, halign: 'right' }
          }
        });
        break;

      case 'Profit & Loss':
        // Revenue Section
        doc.setFontSize(14);
        doc.text('Profit & Loss Statement', 14, yPos);
        yPos += 10;

        const revenueData = [
          ['Total Revenue', data.revenue.total.toLocaleString()],
          ['Cost of Goods Sold', data.revenue.cogs.toLocaleString()],
          ['Gross Profit', data.revenue.grossProfit.toLocaleString()],
          ['Gross Margin', `${data.revenue.grossMargin.toFixed(1)}%`],
        ];

        autoTable(doc, {
          startY: yPos,
          head: [['Revenue Metrics', 'Value']],
          body: revenueData,
          theme: 'striped',
          headStyles: { fillColor: [76, 175, 80] },
          columnStyles: {
            0: { fontStyle: 'bold' },
            1: { halign: 'right' }
          }
        });

        yPos = doc.lastAutoTable.finalY + 15;

        // Key Metrics
        doc.setFontSize(14);
        doc.text('Key Metrics', 14, yPos);

        const metricsData = [
          ['Total Transactions', data.metrics.totalTransactions.toString()],
          ['Avg. Transaction Value', data.metrics.averageTransactionValue.toFixed(2)],
          ['Profit Margin', `${data.metrics.profitMargin.toFixed(1)}%`],
        ];

        autoTable(doc, {
          startY: yPos + 10,
          head: [['Metric', 'Value']],
          body: metricsData,
          theme: 'striped',
          headStyles: { fillColor: [76, 175, 80] },
          columnStyles: {
            0: { fontStyle: 'bold' },
            1: { halign: 'right' }
          }
        });
        break;
    }

    // Add footer with page number
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const convertToCSV = (data) => {
    let csvContent = '';

    switch (tabValue) {
      case 0: // Sales Report
        csvContent = 'Sales Report\n';
        csvContent += `Period: ${dateRange.startDate} to ${dateRange.endDate}\n\n`;
        csvContent += 'Summary\n';
        csvContent += `Total Sales,${salesReport.summary.totalSales}\n`;
        csvContent += `Total Revenue,${salesReport.summary.totalRevenue}\n`;
        csvContent += `Total Cost,${salesReport.summary.totalCost}\n`;
        csvContent += `Total Profit,${salesReport.summary.totalProfit}\n\n`;

        csvContent += 'Top Selling Medicines\n';
        csvContent += 'Medicine,Generic Name,Quantity Sold,Revenue,Cost,Profit\n';
        salesReport.topSellingMedicines.forEach(med => {
          csvContent += `${med.medicine.name},${med.medicine.genericName},${med.quantity},${med.revenue},${med.cost},${med.revenue - med.cost}\n`;
        });
        break;

      case 1: // Inventory Report
        csvContent = 'Inventory Report\n\n';
        csvContent += 'Summary\n';
        csvContent += `Total Medicines,${inventoryReport.summary.totalMedicines}\n`;
        csvContent += `Low Stock Items,${inventoryReport.summary.lowStockCount}\n`;
        csvContent += `Expiring Soon,${inventoryReport.summary.expiringCount}\n`;
        csvContent += `Total Value,${inventoryReport.summary.totalValue}\n\n`;

        csvContent += 'Inventory by Category\n';
        csvContent += 'Category,Medicines,Total Quantity,Total Value\n';
        inventoryReport.inventoryByCategory.forEach(cat => {
          csvContent += `${cat.category},${cat.medicines},${cat.totalQuantity},${cat.totalValue}\n`;
        });
        break;

      case 2: // Profit & Loss
        csvContent = 'Profit & Loss Report\n';
        csvContent += `Period: ${dateRange.startDate} to ${dateRange.endDate}\n\n`;

        csvContent += 'Revenue\n';
        csvContent += `Total Revenue,${profitLossReport.revenue.total}\n`;
        csvContent += `Cost of Goods Sold,${profitLossReport.revenue.cogs}\n`;
        csvContent += `Gross Profit,${profitLossReport.revenue.grossProfit}\n`;
        csvContent += `Gross Margin,${profitLossReport.revenue.grossMargin}%\n\n`;

        csvContent += 'Key Metrics\n';
        csvContent += `Total Transactions,${profitLossReport.metrics.totalTransactions}\n`;
        csvContent += `Average Transaction Value,${profitLossReport.metrics.averageTransactionValue}\n`;
        csvContent += `Profit Margin,${profitLossReport.metrics.profitMargin}%\n`;
        break;
    }

    return csvContent;
  };

  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      let filename = '';
      let data = null;
      let reportType = '';

      switch (tabValue) {
        case 0: // Sales Report
          if (!salesReport) {
            enqueueSnackbar('No sales data available to export', { variant: 'warning' });
            return;
          }
          filename = `Sales_Report_${timestamp}`;
          data = salesReport;
          reportType = 'Sales';
          break;

        case 1: // Inventory Report
          if (!inventoryReport) {
            enqueueSnackbar('No inventory data available to export', { variant: 'warning' });
            return;
          }
          filename = `Inventory_Report_${timestamp}`;
          data = inventoryReport;
          reportType = 'Inventory';
          break;

        case 2: // Profit & Loss
          if (!profitLossReport) {
            enqueueSnackbar('No profit/loss data available to export', { variant: 'warning' });
            return;
          }
          filename = `Profit_Loss_Report_${timestamp}`;
          data = profitLossReport;
          reportType = 'Profit & Loss';
          break;
      }

      switch (format) {
        case 'csv':
          exportToCSV(data, filename);
          enqueueSnackbar('Report exported as CSV successfully!', { variant: 'success' });
          break;

        case 'excel':
          exportToExcel(data, filename);
          enqueueSnackbar('Report exported as Excel successfully!', { variant: 'success' });
          break;

        case 'pdf':
          await exportToPDF(data, filename, reportType);
          enqueueSnackbar('Report exported as PDF successfully!', { variant: 'success' });
          break;
      }
    } catch (error) {
      enqueueSnackbar('Error exporting report', { variant: 'error' });
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };

  // Function to export specific table as PDF (for table-specific exports)
  const exportTableAsPDF = async (tableId, filename) => {
    const input = document.getElementById(tableId);
    if (!input) return;

    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 190;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
    pdf.save(`${filename}.pdf`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Reports & Analytics
        </Typography>
        <ExportMenu onExport={handleExport} loading={exportLoading} />
      </Box>

      {/* Date Range Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <DateRange color="action" />
            </Grid>
            <Grid item>
              <TextField
                type="date"
                label="Start Date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item>
              <TextField
                type="date"
                label="End Date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={fetchReports}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab icon={<TrendingUp />} label="Sales Report" />
            <Tab icon={<Inventory />} label="Inventory Report" />
            <Tab icon={<AccountBalance />} label="Profit & Loss" />
          </Tabs>

          {loading && <LinearProgress />}

          {/* Sales Report Tab */}
          <TabPanel value={tabValue} index={0}>
            {salesReport && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Sales Summary ({new Date(salesReport.period.start).toLocaleDateString()} - {new Date(salesReport.period.end).toLocaleDateString()})
                  </Typography>
                  <IconButton
                    onClick={() => exportTableAsPDF('sales-table', `sales-report-${new Date().toISOString().split('T')[0]}`)}
                    title="Export Table as PDF"
                  >
                    <PictureAsPdf />
                  </IconButton>
                </Box>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">
                          {salesReport.summary.totalSales}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Sales
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="success.main">
                          ₹{salesReport.summary.totalRevenue.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Revenue
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="info.main">
                          ₹{salesReport.summary.totalCost.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Cost
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="warning.main">
                          ₹{salesReport.summary.totalProfit.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Profit
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Top Selling Medicines */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Top Selling Medicines
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" id="sales-table">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Medicine</TableCell>
                            <TableCell align="center">Quantity Sold</TableCell>
                            <TableCell align="right">Revenue</TableCell>
                            <TableCell align="right">Cost</TableCell>
                            <TableCell align="right">Profit</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {salesReport.topSellingMedicines.map((medicine, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="subtitle2">
                                  {medicine.medicine.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {medicine.medicine.genericName}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={medicine.quantity} size="small" />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="subtitle2" color="success.main">
                                  ₹{medicine.revenue.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  ₹{medicine.cost.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="subtitle2" fontWeight="bold">
                                  ₹{(medicine.revenue - medicine.cost).toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            )}
          </TabPanel>

          {/* Inventory Report Tab */}
          <TabPanel value={tabValue} index={1}>
            {inventoryReport && (
              <Box>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Inventory Overview
                </Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold">
                          {inventoryReport.summary.totalMedicines}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Medicines
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="warning.main">
                          {inventoryReport.summary.lowStockCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Low Stock Items
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="error.main">
                          {inventoryReport.summary.expiringCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Expiring Soon
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="success.main">
                          ₹{inventoryReport.summary.totalValue.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Value
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Inventory by Category */}
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        Inventory by Category
                      </Typography>
                      <IconButton
                        onClick={() => exportTableAsPDF('inventory-table', `inventory-report-${new Date().toISOString().split('T')[0]}`)}
                        title="Export Table as PDF"
                      >
                        <PictureAsPdf />
                      </IconButton>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" id="inventory-table">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell align="center">Medicines</TableCell>
                            <TableCell align="center">Total Quantity</TableCell>
                            <TableCell align="right">Total Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inventoryReport.inventoryByCategory.map((category, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="subtitle2">
                                  {category.category}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={category.medicines} size="small" />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="subtitle2">
                                  {category.totalQuantity}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="subtitle2" color="success.main">
                                  ₹{category.totalValue.toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            )}
          </TabPanel>

          {/* Profit & Loss Tab */}
          <TabPanel value={tabValue} index={2}>
            {profitLossReport && (
              <Box>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Profit & Loss Statement ({new Date(profitLossReport.period.start).toLocaleDateString()} - {new Date(profitLossReport.period.end).toLocaleDateString()})
                </Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Revenue
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Total Revenue:</Typography>
                          <Typography variant="h6" color="success.main">
                            ₹{profitLossReport.revenue.total.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Cost of Goods Sold:</Typography>
                          <Typography color="error.main">
                            ₹{profitLossReport.revenue.cogs.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Gross Profit:</Typography>
                          <Typography variant="h6" color="primary.main">
                            ₹{profitLossReport.revenue.grossProfit.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography>Gross Margin:</Typography>
                          <Typography variant="h6" color="primary.main">
                            {profitLossReport.revenue.grossMargin.toFixed(1)}%
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Key Metrics
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Total Transactions:</Typography>
                          <Typography>{profitLossReport.metrics.totalTransactions}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Avg. Transaction Value:</Typography>
                          <Typography>₹{profitLossReport.metrics.averageTransactionValue.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography>Profit Margin:</Typography>
                          <Typography variant="h6" color="success.main">
                            {profitLossReport.metrics.profitMargin.toFixed(1)}%
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};